// File: src/pages/Users/Users.jsx

import React, { useState, useEffect } from "react";
import {
  Container,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  MenuItem,
  Box,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Chip,
  IconButton
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import axios from "../../axios";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PeopleIcon from "@mui/icons-material/People";
import SecurityIcon from "@mui/icons-material/Security";
import StoreIcon from "@mui/icons-material/Store"; // For Assign Context
import VpnKeyIcon from "@mui/icons-material/VpnKey"; // For Reset Password
import rolesApi from '../../api/rolesApi';
import { toast } from 'react-toastify';
import ResetPasswordDialog from "./ResetPasswordDialog";
import { useAuth } from '../../context/AuthContext'; // Import useAuth

import { useNavigate } from "react-router-dom"; // ✅ استيراد useNavigate

export default function UsersPage() {
  const navigate = useNavigate(); // ✅ تعريف navigate
  const { hasPermission } = useAuth(); // Access auth context
  const currentUserName = localStorage.getItem('userName') || "";

  // Check if user is authorized (Owner, Admin, or has User.Manage)
  const isAuthorized = currentUserName.toLowerCase() === 'owner' || currentUserName.toLowerCase() === 'admin' || hasPermission('User.Manage');

  // حفظ بيانات النموذج والقائمة
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [roles, setRoles] = useState([]); // Available roles
  const [safes, setSafes] = useState([]); // Available Safes
  const [stocks, setStocks] = useState([]); // Available Stocks
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingBranches, setLoadingBranches] = useState(false);

  // Role Assignment State
  const [openRoleDialog, setOpenRoleDialog] = useState(false);
  const [openAddUserDialog, setOpenAddUserDialog] = useState(false); // ✅ حالة نافذة إضافة مستخدم
  const [openContextDialog, setOpenContextDialog] = useState(false); // ✅ حالة نافذة ربط الصندوق/المستودع
  const [openResetPasswordDialog, setOpenResetPasswordDialog] = useState(false); // ✅ حالة نافذة إعادة تعيين كلمة المرور
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);
  const [selectedSafeIds, setSelectedSafeIds] = useState([]);
  const [selectedStockIds, setSelectedStockIds] = useState([]);
  const [selectedBranchIds, setSelectedBranchIds] = useState([]);

  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [mobile, setMobile] = useState(""); // ✅ إضافة حقل الجوال
  const [branchId, setBranchId] = useState("");
  const [message, setMessage] = useState(null);
  const [severity, setSeverity] = useState("success");
  const [submitting, setSubmitting] = useState(false);

  // جلب companyId من التخزين المحلي
  const storedCompanyId = localStorage.getItem("companyId");
  const companyId = storedCompanyId ? parseInt(storedCompanyId, 10) : null;

  // دالة لجلب الفروع المرتبطة بالشركة
  useEffect(() => {
    if (!companyId) return;
    setLoadingBranches(true);

    axios
      .get(`/Branch/by-company/${companyId}`)
      .then((res) => {
        setBranches(res.data);
      })
      .catch((err) => {
        console.error("❌ فشل في جلب الفروع:", err);
      })
      .finally(() => setLoadingBranches(false));
  }, [companyId]);

  // دالة لجلب المستخدمين المرتبطين بالشركة
  const fetchUsers = () => {
    if (!companyId) return;
    setLoadingUsers(true);

    axios
      .get(`/User/AllUser/${companyId}`)
      .then((res) => {
        // If API returns { success, count, users: [...] }
        if (res.data && res.data.users) {
            setUsers(res.data.users);
        } else if (Array.isArray(res.data)) {
            // Fallback if API returns array directly
            setUsers(res.data);
        } else {
            setUsers([]);
        }
      })
      .catch((err) => {
        console.error("❌ فشل في جلب المستخدمين:", err);
        setUsers([]);
      })
      .finally(() => setLoadingUsers(false));
  };

  useEffect(() => {
    if (companyId) {
        rolesApi.getAll(companyId).then(res => setRoles(res.data));
        
        // Fetch Safes
        axios.get(`/Safe/by-company/${companyId}`)
            .then(res => setSafes(res.data || []))
            .catch(console.error);

        // Fetch Stocks
        axios.get(`/Stock/by-company/${companyId}`)
            .then(res => setStocks(res.data || []))
            .catch(console.error);
    }
    fetchUsers();
  }, [companyId]);

  if (!isAuthorized) {
    return (
      <Box p={3} dir="rtl" textAlign="center" mt={5}>
        <SecurityIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h5" color="text.secondary" fontWeight="bold">
          عذراً، ليس لديك صلاحية للوصول إلى هذه الصفحة.
        </Typography>
        <Typography variant="body1" color="text.secondary" mt={1}>
          هذه الصفحة مخصصة للمدير العام (Owner) أو من يملك صلاحية إدارة المستخدمين.
        </Typography>
      </Box>
    );
  }

  const handleOpenRoleDialog = (user) => {
    setSelectedUser(user);
    // user.roles is expected to be array of objects { roleId, name } from API
    // or we need to map from the datagrid row
    const currentRoleIds = user.roles ? user.roles.map(r => r.roleId) : [];
    setSelectedRoleIds(currentRoleIds);
    setOpenRoleDialog(true);
  };

  const handleOpenContextDialog = (user) => {
    setSelectedUser(user);
    setSelectedSafeIds(user.allowedSafes || []);
    setSelectedStockIds(user.allowedStocks || []);
    setSelectedBranchIds(user.allowedBranches && user.allowedBranches.length > 0
      ? user.allowedBranches
      : (user.branch?.id ? [user.branch.id] : []));
    setOpenContextDialog(true);
  };

  const handleSaveContext = async () => {
    if (!selectedUser) return;
    try {
        await axios.post('/User/AssignContext', {
            userId: selectedUser.id,
            safeIds: selectedSafeIds,
            stockIds: selectedStockIds,
            branchIds: selectedBranchIds
        });
        toast.success('تم تحديث ارتباطات المستخدم بنجاح');
        setOpenContextDialog(false);
        fetchUsers();
    } catch (err) {
        toast.error('فشل تحديث الارتباطات');
        console.error(err);
    }
  };

  const handleSaveRoles = async () => {
    if (!selectedUser) return;
    try {
        await axios.post('/User/AssignRoles', {
            userId: selectedUser.id,
            roleIds: selectedRoleIds
        });
        toast.success('تم تحديث أدوار المستخدم بنجاح');
        setOpenRoleDialog(false);
        fetchUsers(); // Refresh to show new roles
    } catch (err) {
        toast.error('فشل تحديث الأدوار');
    }
  };

  // عند الضغط على زر حفظ المستخدم
  const handleSubmit = async (e) => {
  e.preventDefault();
  setMessage(null);

  if (!userName.trim() || !password.trim() || !branchId || !mobile.trim()) {
    setSeverity("error");
    setMessage("❌ يرجى تعبئة جميع الحقول المطلوبة.");
    return;
  }

  setSubmitting(true);
  try {
    const response = await axios.post("/User/register", {
      userName: userName.trim(),
      password: password.trim(),
      mobile: mobile.trim(),
      branchId: Number(branchId),
    });

    // ✅ البيانات الحقيقية هنا
    const { success, message } = response.data;

    if (!success) {
      throw new Error(message || "❌ فشل في إنشاء المستخدم.");
    }

    // ✅ نجاح
    setSeverity("success");
    setMessage(message);
    toast.success(message);

    fetchUsers();

    setUserName("");
    setPassword("");
    setMobile("");
    setBranchId("");
    setOpenAddUserDialog(false);

  } catch (err) {
    // ✅ قراءة رسالة الخطأ من السيرفر
    const serverMsg =
      err.response?.data?.message ||
      err.message ||
      "❌ فشل في إنشاء المستخدم.";

    setSeverity("error");
    setMessage(serverMsg);
    toast.error(serverMsg);

  } finally {
    setSubmitting(false);
  }
};


  // تعريف أعمدة DataGrid
  const columns = [
    { field: "id", headerName: "معرّف", width: 80 },
    { field: "userName", headerName: "اسم المستخدم", flex: 1, minWidth: 50 },
    { field: "mobile", headerName: "رقم الجوال", flex: 1, minWidth: 50 }, // ✅ إضافة عمود الجوال
    { field: "companyName", headerName: "الشركة", flex: 1, minWidth: 50 },
    { field: "branchName", headerName: "الفرع", flex: 1, minWidth: 50 },
    { 
        field: "roles", 
        headerName: "الأدوار", 
        flex: 1.5,
        renderCell: (params) => (
            <Box display="flex" gap={0.5} flexWrap="wrap">
                {params.value.map((roleName, idx) => (
                    <Chip key={idx} label={roleName} size="small" variant="outlined" />
                ))}
            </Box>
        )
    },
    {
        field: "actions",
        headerName: "إجراءات",
        width: 150,
        renderCell: (params) => (
            <Box>
                <IconButton color="primary" onClick={() => handleOpenRoleDialog(params.row.originalUser)} title="إدارة الأدوار">
                    <SecurityIcon />
                </IconButton>
                <IconButton color="secondary" onClick={() => handleOpenContextDialog(params.row.originalUser)} title="ربط الصندوق/المستودع">
                    <StoreIcon />
                </IconButton>
                <IconButton color="warning" onClick={() => { setSelectedUser(params.row.originalUser); setOpenResetPasswordDialog(true); }} title="إعادة تعيين كلمة المرور">
                    <VpnKeyIcon />
                </IconButton>
            </Box>
        )
    }
  ];

  // تحضير الصفوف للعرض
  const rows = users.map((u) => ({
    id: u.id,
    userName: u.userName,
    mobile: u.mobile, // ✅ إضافة بيانات الجوال
    companyName: u.company?.name || "—",
    branchName: u.branch?.name || "—",
    roles: u.roles ? u.roles.map(r => r.name) : [],
    originalUser: u // Pass full user object for actions
  }));

  return (
    <Container maxWidth="xl" sx={{ py: 5 }} dir="rtl">
      {/* Header Section */}
      <Box 
        display="flex" 
        justifyContent="space-between" 
        alignItems="center" 
        mb={5} 
        sx={{ 
          background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)', 
          p: 4, 
          borderRadius: 4, 
          color: 'white',
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
        }}
      >
        <Box display="flex" alignItems="full" gap={3}>
            <Box sx={{ bgcolor: 'rgba(255,255,255,0.2)', p: 1.5, borderRadius: 2 }}>
                <PeopleIcon sx={{ fontSize: 40, color: 'white' }} />
            </Box>
            <Box>
                <Typography variant="h4" fontWeight="800" letterSpacing={0.5}>
                    إدارة المستخدمين
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9, mt: 0.5 }}>
                    إدارة حسابات الموظفين، الصلاحيات، والأدوار الوظيفية
                </Typography>
            </Box>
        </Box>
        <Box display="flex" gap={2}>
            <Button 
                variant="contained" 
                sx={{ 
                  bgcolor: 'white', 
                  color: '#1976d2',
                  fontWeight: 'bold',
                  px: 3, 
                  py: 1.5,
                  borderRadius: 3,
                  '&:hover': { bgcolor: '#f5f5f5' }
                }}
                startIcon={<PersonAddIcon />}
                onClick={() => setOpenAddUserDialog(true)}
                size="large"
            >
                إضافة مستخدم
            </Button>
            <Button 
                variant="contained" 
                sx={{ 
                  bgcolor: 'rgba(255,255,255,0.2)', 
                  color: 'white',
                  fontWeight: 'bold',
                  px: 3, 
                  py: 1.5,
                  borderRadius: 3,
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
                }}
                startIcon={<SecurityIcon />}
                onClick={() => navigate('/roles')}
                size="large"
            >
                إدارة الأدوار
            </Button>
        </Box>
      </Box>

      <Paper 
        elevation={0} 
        sx={{ 
          p: 0, 
          borderRadius: 4, 
          border: '1px solid #e0e0e0',
          overflow: 'hidden',
          bgcolor: 'white'
        }}
      >
        <Box 
            p={3} 
            borderBottom="1px solid #f0f0f0" 
            bgcolor="#f8f9fa"
            display="flex" 
            justifyContent="space-between" 
            alignItems="center"
        >
            <Box display="flex" alignItems="center" gap={2}>
                <PeopleIcon color="primary" />
                <Typography variant="h6" fontWeight="bold" color="#37474f">
                    قائمة الموظفين
                </Typography>
            </Box>
            <Chip 
                label={`${users.length} مستخدم نشط`} 
                color="primary" 
                variant="outlined"
                sx={{ fontWeight: 'bold', borderRadius: 2 }} 
            />
        </Box>

        <Box sx={{ height: 600, width: '100%', p: 1 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loadingUsers}
            pageSize={10}
            rowsPerPageOptions={[10, 20, 50]}
            disableSelectionOnClick
            sx={{
              border: 'none',
              "& .MuiDataGrid-columnHeaders": {
                bgcolor: '#fff',
                borderBottom: '2px solid #f0f0f0',
                color: '#546e7a',
                fontSize: '0.95rem'
              },
              "& .MuiDataGrid-columnHeader": {
                fontWeight: "700",
              },
              "& .MuiDataGrid-row": {
                '&:hover': { bgcolor: '#f5f5f5' },
                borderBottom: '1px solid #f9f9f9'
              },
              "& .MuiDataGrid-cell": {
                borderBottom: 'none'
              }
            }}
          />
        </Box>
      </Paper>

      {/* Add User Dialog */}
      <Dialog open={openAddUserDialog} onClose={() => setOpenAddUserDialog(false)} maxWidth="sm" fullWidth dir="rtl">
        <DialogTitle sx={{ bgcolor: '#f5f5f5', borderBottom: '1px solid #ddd', fontWeight: 'bold' }}>
            <Box display="flex" alignItems="center" gap={1}>
                <PersonAddIcon color="primary" />
                إضافة مستخدم جديد
            </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3, mt: 1 }}>
            {message && severity === 'error' && (
              <Alert severity={severity} sx={{ mb: 3, borderRadius: 2, mt: 2 }}>
                {message}
              </Alert>
            )}
            <Box component="form" id="addUserForm" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
              <TextField
                margin="normal"
                fullWidth
                label="اسم المستخدم"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                required
                variant="outlined"
                InputProps={{ sx: { borderRadius: 2 } }}
              />
              <TextField
                margin="normal"
                fullWidth
                label="رقم الجوال"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                required
                variant="outlined"
                placeholder="05xxxxxxxx"
                InputProps={{ sx: { borderRadius: 2 } }}
              />
              <TextField
                margin="normal"
                fullWidth
                label="كلمة المرور"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                variant="outlined"
                InputProps={{ sx: { borderRadius: 2 } }}
              />
              <TextField
                margin="normal"
                select
                fullWidth
                label="اختر الفرع"
                value={branchId}
                onChange={(e) => setBranchId(e.target.value)}
                required
                variant="outlined"
                InputProps={{ sx: { borderRadius: 2 } }}
              >
                <MenuItem value="">
                  <em>— اختر فرع —</em>
                </MenuItem>
                {branches.map((b) => (
                  <MenuItem key={b.id} value={b.id}>
                    {b.name}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #ddd', bgcolor: '#f9f9f9' }}>
            <Button onClick={() => setOpenAddUserDialog(false)} color="inherit" size="large">إلغاء</Button>
            <Button 
                type="submit" 
                form="addUserForm"
                variant="contained" 
                size="large"
                disabled={submitting}
                disableElevation
                sx={{ px: 4 }}
            >
                {submitting ? "جاري الحفظ..." : "حفظ"}
            </Button>
        </DialogActions>
      </Dialog>

      {/* Role Assignment Dialog */}
      <Dialog open={openRoleDialog} onClose={() => setOpenRoleDialog(false)} maxWidth="sm" fullWidth dir="rtl">
        <DialogTitle>إدارة أدوار المستخدم: {selectedUser?.userName}</DialogTitle>
        <DialogContent dividers>
            <FormControl sx={{ m: 1, width: '100%' }}>
                <InputLabel>الأدوار</InputLabel>
                <Select
                    multiple
                    value={selectedRoleIds}
                    onChange={(e) => setSelectedRoleIds(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                    input={<OutlinedInput label="الأدوار" />}
                    renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((value) => (
                                <Chip key={value} label={roles.find(r => r.id === value)?.name} />
                            ))}
                        </Box>
                    )}
                >
                    {roles.map((role) => (
                        <MenuItem key={role.id} value={role.id}>
                            {role.name}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        </DialogContent>
        <DialogActions>
            <Button onClick={() => setOpenRoleDialog(false)} color="inherit">إلغاء</Button>
            <Button onClick={handleSaveRoles} variant="contained" color="primary">حفظ</Button>
        </DialogActions>
      </Dialog>

      {/* Context Assignment Dialog (Branch/Safe/Stock) */}
      <Dialog open={openContextDialog} onClose={() => setOpenContextDialog(false)} maxWidth="sm" fullWidth dir="rtl">
        <DialogTitle sx={{ bgcolor: '#f5f5f5', borderBottom: '1px solid #ddd', fontWeight: 'bold' }}>
            <Box display="flex" alignItems="center" gap={1}>
                <StoreIcon color="secondary" />
                ربط الفرع والصندوق والمستودع: {selectedUser?.userName}
            </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3, mt: 1 }}>
            <Alert severity="success" sx={{ mb: 2, bgcolor: '#e8f5e9', '& .MuiAlert-message': { fontWeight: 'bold' } }}>
                🔓 إذا لم تختِر أي عنصر (غير محدد) سيكون للمستخدم صلاحية على <strong>الكل</strong> (جميع الفروع / الصناديق / المستودعات).
            </Alert>
            <Alert severity="info" sx={{ mb: 2 }}>
                عند تحديد فرع / صندوق / مستودع هنا، سيتم تقييد المستخدم للعمل فقط على ما تم تحديده.
            </Alert>
            <Box display="flex" flexDirection="column" gap={2} mt={1}>
                <FormControl fullWidth>
                    <InputLabel>الفروع المقيدة</InputLabel>
                    <Select
                        multiple
                        value={selectedBranchIds}
                        onChange={(e) => setSelectedBranchIds(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                        label="الفروع المقيدة"
                        renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.length === 0 ? (
                                    <Chip 
                                        key="none" 
                                        label="🔓 غير محدد (كل الفروع)" 
                                        sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 'bold' }} 
                                    />
                                ) : (
                                    selected.map((value) => (
                                        <Chip key={value} label={branches.find(b => b.id === value)?.name} />
                                    ))
                                )}
                            </Box>
                        )}
                    >
                        <MenuItem 
                            value="__ALL__" 
                            disabled
                            sx={{ display: 'none' }}
                        >
                            غير محدد
                        </MenuItem>
                        {branches.map((b) => (
                            <MenuItem key={b.id} value={b.id}>
                                {b.name}
                            </MenuItem>
                        ))}
                    </Select>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                        <Button 
                            size="small" 
                            onClick={() => setSelectedBranchIds([])}
                            disabled={selectedBranchIds.length === 0}
                            sx={{ color: '#2e7d32', fontWeight: 'bold' }}
                        >
                            🔓 غير محدد (مسح الكل)
                        </Button>
                    </Box>
                </FormControl>

                <FormControl fullWidth>
                    <InputLabel>الصناديق المقيدة</InputLabel>
                    <Select
                        multiple
                        value={selectedSafeIds}
                        onChange={(e) => setSelectedSafeIds(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                        label="الصناديق المقيدة"
                        renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.length === 0 ? (
                                    <Chip 
                                        key="none" 
                                        label="🔓 غير محدد (كل الصناديق)" 
                                        sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 'bold' }} 
                                    />
                                ) : (
                                    selected.map((value) => (
                                        <Chip key={value} label={safes.find(s => s.safeId === value)?.name} />
                                    ))
                                )}
                            </Box>
                        )}
                    >
                        {safes.map((safe) => (
                            <MenuItem key={safe.safeId} value={safe.safeId}>
                                {safe.name}
                            </MenuItem>
                        ))}
                    </Select>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                        <Button 
                            size="small" 
                            onClick={() => setSelectedSafeIds([])}
                            disabled={selectedSafeIds.length === 0}
                            sx={{ color: '#2e7d32', fontWeight: 'bold' }}
                        >
                            🔓 غير محدد (مسح الكل)
                        </Button>
                    </Box>
                </FormControl>

                <FormControl fullWidth>
                    <InputLabel>المستودعات المقيدة</InputLabel>
                    <Select
                        multiple
                        value={selectedStockIds}
                        onChange={(e) => setSelectedStockIds(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                        label="المستودعات المقيدة"
                        renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.length === 0 ? (
                                    <Chip 
                                        key="none" 
                                        label="🔓 غير محدد (كل المستودعات)" 
                                        sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 'bold' }} 
                                    />
                                ) : (
                                    selected.map((value) => (
                                        <Chip key={value} label={stocks.find(s => s.id === value)?.name} />
                                    ))
                                )}
                            </Box>
                        )}
                    >
                        {stocks.map((stock) => (
                            <MenuItem key={stock.id} value={stock.id}>
                                {stock.name}
                            </MenuItem>
                        ))}
                    </Select>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                        <Button 
                            size="small" 
                            onClick={() => setSelectedStockIds([])}
                            disabled={selectedStockIds.length === 0}
                            sx={{ color: '#2e7d32', fontWeight: 'bold' }}
                        >
                            🔓 غير محدد (مسح الكل)
                        </Button>
                    </Box>
                </FormControl>
            </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #ddd', bgcolor: '#f9f9f9' }}>
            <Button onClick={() => setOpenContextDialog(false)} color="inherit" size="large">إلغاء</Button>
            <Button onClick={handleSaveContext} variant="contained" color="secondary" size="large">حفظ الربط</Button>
        </DialogActions>
      </Dialog>

      <ResetPasswordDialog 
        open={openResetPasswordDialog} 
        onClose={() => setOpenResetPasswordDialog(false)} 
        user={selectedUser}
      />
    </Container>
  );
}
